import {
	alephToast, NotificationToast, NotificationType, translate,
} from '@aleph/shared-components';
import { captureException } from '@sentry/react';
import dayjs from 'dayjs';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';
import { Maybe, SpotifyReportsQueryItems, SpotifyReportsQueryReportFiltersOptions } from 'wb-spotify/graphql/generated';

export type ReportRow = SpotifyReportsQueryItems;

export const getReportClientIds = (
	reportFilterOptions?: Maybe<Array<Maybe<SpotifyReportsQueryReportFiltersOptions>>>,
): string[] => (reportFilterOptions ?? []).find(filter => filter?.filter === 'filter')?.value ?? [];

export const downloadSpotifyReport = async (report: ReportRow) => {
	try {
		const reportContentCSV = await fetch(
			`${import.meta.env.VITE_API_PATH}/pn_sp/report/${report.reportToken}/lang/en/format/csv`,
		);

		const downloadLink = document.createElement('a');
		downloadLink.download = `${report.reportName}.csv`;
		downloadLink.href = window.URL?.createObjectURL?.(await reportContentCSV.blob());
		downloadLink.click();
	} catch (e) {
		captureException(e);

		alephToast(
			NotificationToast({
				type:  NotificationType.Error,
				title: translate('re: reports.download_err', { defaultValue: 'Error downloading report' }),
			}),
		);
	}
};

export const getScheduleName = (selectedSchedule?: SpotifyReportSchedule) => {
	switch (selectedSchedule) {
		case SpotifyReportSchedule.OnlyOnce:
			return 'Day';
		case SpotifyReportSchedule.EveryMonday:
			return 'Monday';
		case SpotifyReportSchedule.EveryWeekday:
			return 'Sunday';
		case SpotifyReportSchedule.FirstOfTheMonth:
			return 'First day of the month';
		default:
			return null;
	}
};

export function getDatesFromSchedule(
	{ selectedSchedule, maxDate = dayjs() }: { selectedSchedule?: SpotifyReportSchedule, maxDate?: dayjs.Dayjs },
) {
	switch (selectedSchedule) {
		case SpotifyReportSchedule.OnlyOnce:
			return {
				from: dayjs(),
				to:   maxDate,
			};

		case SpotifyReportSchedule.EveryWeekday:
			return {
				from: dayjs().subtract(1, 'week').endOf('week'),
				to:   maxDate,
			};

		case SpotifyReportSchedule.EveryMonday:
			return {
				from: dayjs().startOf('week'),
				to:   dayjs().endOf('week'),
			};

		case SpotifyReportSchedule.FirstOfTheMonth:
			return {
				from: dayjs().startOf('month'),
				to:   dayjs().endOf('month'),
			};

		default:
			return {
				from: dayjs().subtract(1, 'week'),
				to:   dayjs(),
			};
	}
}
